import React, { useState, useRef, useEffect } from "react";
import { Plus, Trash2, Save, Upload, FileText, ListChecks, Truck, Clock, DollarSign, MapPin, Loader2, ExternalLink, Printer, Bookmark, BookMarked, X, Lock, ReceiptText, FileSignature, UserPlus, ChevronDown, ChevronRight, Mail, Camera, Image as ImageIcon, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const uid = () => Math.random().toString(36).slice(2, 10);
const inputCls = "w-full px-3 py-2 rounded-lg dark-input text-sm";

const Section = ({ icon: Icon, title, onAdd, onSave, saving, extra, children }) => (
  <div className="glass-panel rounded-2xl p-6">
    <div className="flex items-center justify-between mb-4">
      <h3 className="font-bold text-white flex items-center gap-2 text-sm uppercase tracking-wider"><Icon size={16} className="text-primary-400" /> {title}</h3>
      <div className="flex gap-2">
        {extra}
        {onAdd && <button onClick={onAdd} className="px-3 py-1.5 bg-white/10 text-white rounded-lg text-xs font-bold hover:bg-white/20 transition flex items-center"><Plus size={12} className="mr-1" /> Add</button>}
        {onSave && <button onClick={onSave} disabled={saving} className="px-3 py-1.5 bg-primary-600 text-white rounded-lg text-xs font-bold hover:bg-primary-500 transition flex items-center disabled:opacity-60"><Save size={12} className="mr-1" /> Save</button>}
      </div>
    </div>
    {children}
  </div>
);

export default function WorkspaceModules({ workflowId, workflow, by = "client", onChange }) {
  const [ros, setRos] = useState(workflow.run_of_show || []);
  const [vendors, setVendors] = useState(workflow.vendors || []);
  const [tasks, setTasks] = useState(workflow.tasks || []);
  const [budget, setBudget] = useState(workflow.budget || []);
  const [details, setDetails] = useState(workflow.event_details || {});
  const [files, setFiles] = useState(workflow.files || []);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [directory, setDirectory] = useState([]);
  const [showDir, setShowDir] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [privNotes, setPrivNotes] = useState([]);
  const [privDraft, setPrivDraft] = useState("");
  const [privLoaded, setPrivLoaded] = useState(false);
  const [addingPriv, setAddingPriv] = useState(false);
  const [vendorNoteDraft, setVendorNoteDraft] = useState({});
  const [receiptCat, setReceiptCat] = useState("receipt");
  const [receiptVendor, setReceiptVendor] = useState("");
  const [recapText, setRecapText] = useState(workflow.recap?.text || "");
  const [recapBusy, setRecapBusy] = useState("");
  const fileRef = useRef(null);
  const receiptRef = useRef(null);
  const photoRef = useRef(null);
  const isAdmin = by === "admin";

  useEffect(() => {
    setRos(workflow.run_of_show || []);
    setVendors(workflow.vendors || []);
    setTasks(workflow.tasks || []);
    setBudget(workflow.budget || []);
    setDetails(workflow.event_details || {});
    setFiles(workflow.files || []);
    setRecapText(workflow.recap?.text || "");
  }, [workflow.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isAdmin) {
      api.listVendorDirectory().then(setDirectory).catch(() => {});
      api.getPrivateNotes(workflowId).then((d) => { setPrivNotes(d.notes || []); setPrivLoaded(true); }).catch(() => setPrivLoaded(true));
    }
  }, [isAdmin, workflowId]);

  const fmtTime = (iso) => {
    try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); }
    catch { return ""; }
  };

  const save = async (patch) => {
    setSaving(true);
    try {
      const updated = await api.updateWorkspaceModules(workflowId, patch);
      onChange?.(updated);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const addPrivNote = async () => {
    const text = privDraft.trim();
    if (!text) return;
    setAddingPriv(true);
    try {
      const note = await api.addPrivateNote(workflowId, text);
      setPrivNotes((n) => [note, ...n]);
      setPrivDraft("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add note");
    } finally { setAddingPriv(false); }
  };

  const deletePrivNote = async (noteId) => {
    setPrivNotes((n) => n.filter((x) => x.id !== noteId));
    try { await api.deletePrivateNote(workflowId, noteId); } catch { /* ignore */ }
  };

  const primaryContact = (v) => {
    const c = (v.contacts || [])[0];
    if (c) return { contact: c.name || v.contact || "", phone: v.phone || c.phone || "", email: v.email || c.email || "" };
    return { contact: v.contact || "", phone: v.phone || "", email: v.email || "" };
  };

  const saveVendorToDir = async (v) => {
    if (!v.name?.trim()) { toast.error("Add a vendor name first"); return; }
    const pc = primaryContact(v);
    const notesText = (v.note_log || []).map((n) => n.text).join(" | ");
    try {
      const rec = await api.saveVendorToDirectory({ name: v.name, category: v.category || "", contact: pc.contact, phone: pc.phone, email: pc.email, address: v.address || "", notes: notesText, default_cost: String(v.cost || "") });
      setDirectory((d) => [rec, ...d.filter((x) => x.id !== rec.id)]);
      toast.success(`Saved "${v.name}" to your vendor directory`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save vendor");
    }
  };

  const addFromDir = (dv) => {
    const nid = uid();
    setVendors((s) => [...s, {
      id: nid, name: dv.name, category: dv.category || "", status: "pending", cost: dv.default_cost || "",
      phone: dv.phone || "", email: dv.email || "", address: dv.address || "", note_log: [],
      contacts: dv.contact ? [{ id: uid(), name: dv.contact || "", role: "Primary", phone: dv.phone || "", email: dv.email || "" }] : [],
    }]);
    setExpanded((e) => ({ ...e, [nid]: true }));
    setShowDir(false);
    toast.success(`Added ${dv.name} — remember to Save`);
  };

  const removeFromDir = async (id) => {
    setDirectory((d) => d.filter((x) => x.id !== id));
    try { await api.deleteVendorFromDirectory(id); } catch { /* ignore */ }
  };

  const patchVendor = (id, patch) => setVendors((vs) => vs.map((x) => x.id === id ? { ...x, ...patch } : x));
  const addVendor = () => { const nid = uid(); setVendors((s) => [...s, { id: nid, name: "", category: "", status: "pending", cost: "", phone: "", email: "", address: "", note_log: [], contacts: [] }]); setExpanded((e) => ({ ...e, [nid]: true })); };
  const addContact = (vid) => patchVendor(vid, { contacts: [...(vendors.find((v) => v.id === vid)?.contacts || []), { id: uid(), name: "", role: "", phone: "", email: "" }] });
  const patchContact = (vid, cid, patch) => { const v = vendors.find((x) => x.id === vid); patchVendor(vid, { contacts: (v.contacts || []).map((c) => c.id === cid ? { ...c, ...patch } : c) }); };
  const removeContact = (vid, cid) => { const v = vendors.find((x) => x.id === vid); patchVendor(vid, { contacts: (v.contacts || []).filter((c) => c.id !== cid) }); };

  const addVendorNote = async (vid) => {
    const text = (vendorNoteDraft[vid] || "").trim();
    if (!text) return;
    const entry = { id: uid(), text, at: new Date().toISOString(), by };
    const nv = vendors.map((v) => v.id === vid ? { ...v, note_log: [entry, ...(v.note_log || [])] } : v);
    setVendors(nv);
    setVendorNoteDraft((d) => ({ ...d, [vid]: "" }));
    await save({ vendors: nv });
  };

  const deleteVendorNote = async (vid, nid) => {
    const nv = vendors.map((v) => v.id === vid ? { ...v, note_log: (v.note_log || []).filter((n) => n.id !== nid) } : v);
    setVendors(nv);
    await save({ vendors: nv });
  };

  const upload = async (f, { category = "document", vendor = "", notify = false } = {}) => {
    if (!f) return;
    setUploading(true);
    try {
      const rec = await api.uploadWorkspaceFile(workflowId, f, by, { category, vendor, notify });
      setFiles((s) => [rec, ...s]);
      if (notify && rec.emailed) toast.success("Uploaded — client notified by email");
      else if (notify && !rec.emailed) toast.success("Uploaded (email not sent — no client email on file)");
      else toast.success("File uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
      if (receiptRef.current) receiptRef.current.value = "";
    }
  };

  const removeFile = async (id) => {
    setFiles((s) => s.filter((f) => f.id !== id));
    try { await api.deleteWorkspaceFile(workflowId, id); } catch { /* ignore */ }
  };

  const receiptFiles = files.filter((f) => ["receipt", "contract"].includes(f.category));
  const photoFiles = files.filter((f) => f.category === "event_photo");
  const docFiles = files.filter((f) => !["receipt", "contract", "event_photo"].includes(f.category));

  const genRecap = async () => {
    setRecapBusy("gen");
    try { const r = await api.generateRecap(workflowId); setRecapText(r.text || ""); toast.success("Recap generated — review & edit"); } catch (e) { toast.error(e?.response?.data?.detail || "Could not generate recap"); } finally { setRecapBusy(""); }
  };
  const saveRecap = async () => {
    setRecapBusy("save");
    try { await api.saveRecap(workflowId, recapText); toast.success("Recap saved"); } catch { toast.error("Could not save"); } finally { setRecapBusy(""); }
  };
  const sendRecap = async () => {
    setRecapBusy("send");
    try { await api.saveRecap(workflowId, recapText); const r = await api.sendRecap(workflowId); toast.success(r.sent ? `Recap + ${r.photos} photos emailed to client` : "Recap saved to Files (email not sent — no client email)"); } catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); } finally { setRecapBusy(""); }
  };

  const FileRow = ({ f }) => (
    <div key={f.id} className="flex items-center gap-3 p-3 rounded-xl bg-black/30 border border-white/5" data-testid="ws-file-row">
      {f.category === "contract" ? <FileSignature size={18} className="text-amber-400 flex-shrink-0" /> : f.category === "receipt" ? <ReceiptText size={18} className="text-green-400 flex-shrink-0" /> : <FileText size={18} className="text-primary-400 flex-shrink-0" />}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold text-white truncate">{f.name}</div>
        <div className="text-[11px] text-zinc-500">
          {f.category && f.category !== "document" ? `${f.category} · ` : ""}{f.vendor ? `${f.vendor} · ` : ""}{f.by} · {(f.uploaded_at || "").slice(0, 10)}
          {f.emailed && <span className="text-green-400 ml-1">· emailed client</span>}
        </div>
      </div>
      <a href={api.workspaceFileUrl(workflowId, f.id)} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 bg-white/10 text-white rounded-lg text-xs font-bold hover:bg-white/20 transition flex items-center">Open <ExternalLink size={12} className="ml-1.5" /></a>
      <button onClick={() => removeFile(f.id)} className="text-zinc-500 hover:text-red-400"><Trash2 size={14} /></button>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="workspace-modules">
      {/* Day Sheet export */}
      <div className="flex items-center justify-between glass-panel rounded-2xl px-5 py-4">
        <div>
          <div className="text-sm font-bold text-white">On-site Day Sheet</div>
          <div className="text-xs text-zinc-500">Run of show + vendors + logistics on one printable page.</div>
        </div>
        <a href={api.workspaceDaySheetUrl(workflowId)} target="_blank" rel="noopener noreferrer" className="px-4 py-2.5 bg-primary-600 text-white rounded-xl text-sm font-bold hover:bg-primary-500 transition flex items-center" data-testid="day-sheet-btn"><Printer size={15} className="mr-2" /> Download Day Sheet</a>
      </div>

      {/* Event Photos — capture on-site, client can see */}
      <Section icon={Camera} title="Event Photos">
        {isAdmin && (
          <div className="mb-4 flex flex-wrap gap-2">
            <button onClick={() => photoRef.current?.click()} disabled={uploading} className="px-4 py-2.5 bg-primary-600 text-white rounded-xl text-sm font-bold hover:bg-primary-500 transition flex items-center disabled:opacity-60" data-testid="capture-photo-btn">
              {uploading ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Camera size={15} className="mr-2" />} Take / Add Photo
            </button>
            <input ref={photoRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={(e) => { Array.from(e.target.files || []).forEach((f) => upload(f, { category: "event_photo" })); if (photoRef.current) photoRef.current.value = ""; }} />
          </div>
        )}
        {photoFiles.length === 0 && <p className="text-zinc-600 text-sm">{isAdmin ? "No photos yet. Tap \"Take / Add Photo\" to capture the event as proof of work — the client can see these too." : "No event photos yet. Your team will add photos here."}</p>}
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
          {photoFiles.map((f) => (
            <div key={f.id} className="relative group aspect-square rounded-xl overflow-hidden border border-white/10 bg-black/40" data-testid="event-photo">
              <a href={api.workspaceFileUrl(workflowId, f.id)} target="_blank" rel="noopener noreferrer"><img src={api.workspaceFileUrl(workflowId, f.id)} alt={f.name} className="w-full h-full object-cover" /></a>
              {isAdmin && <button onClick={() => removeFile(f.id)} className="absolute top-1 right-1 p-1.5 rounded-lg bg-black/70 text-white opacity-0 group-hover:opacity-100 transition" data-testid="delete-photo-btn"><Trash2 size={12} /></button>}
            </div>
          ))}
        </div>
      </Section>

      {/* Event Recap */}
      <Section icon={Sparkles} title="Event Recap"
        extra={isAdmin && (
          <button onClick={genRecap} disabled={recapBusy === "gen"} className="px-3 py-1.5 bg-white/10 text-white rounded-lg text-xs font-bold hover:bg-white/20 transition flex items-center disabled:opacity-60" data-testid="generate-recap-btn">{recapBusy === "gen" ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Sparkles size={12} className="mr-1" />} Generate with AI</button>
        )}
      >
        {isAdmin ? (
          <>
            <textarea value={recapText} onChange={(e) => setRecapText(e.target.value)} rows={7} placeholder="Write a recap of the event, or tap 'Generate with AI' to draft one from your run of show, notes and photos…" className={inputCls} data-testid="recap-input" />
            <div className="flex flex-wrap gap-2 mt-3">
              <button onClick={saveRecap} disabled={recapBusy === "save" || !recapText.trim()} className="px-4 py-2 bg-white/10 text-white rounded-lg text-sm font-bold hover:bg-white/20 transition flex items-center disabled:opacity-60" data-testid="save-recap-btn">{recapBusy === "save" ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />} Save</button>
              <button onClick={sendRecap} disabled={recapBusy === "send" || !recapText.trim()} className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-bold hover:bg-primary-500 transition flex items-center disabled:opacity-60" data-testid="send-recap-btn">{recapBusy === "send" ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Mail size={14} className="mr-2" />} Email Recap + Photos to Client</button>
            </div>
          </>
        ) : (
          recapText ? <div className="text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed" data-testid="recap-view">{recapText}</div>
            : <p className="text-zinc-600 text-sm">Your event recap will appear here after the event.</p>
        )}
      </Section>

      {/* Event Details */}
      <Section icon={MapPin} title="Event Details" onSave={() => save({ event_details: details })} saving={saving}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[["date", "Date"], ["venue", "Venue"], ["headcount", "Headcount"], ["load_in", "Load-in"], ["load_out", "Load-out"], ["point_of_contact", "On-site Contact"]].map(([k, label]) => (
            <div key={k}>
              <label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1">{label}</label>
              <input value={details[k] || ""} onChange={(e) => setDetails({ ...details, [k]: e.target.value })} className={inputCls} data-testid={`event-detail-${k}`} />
            </div>
          ))}
        </div>
        <div className="mt-3">
          <label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1">Key Logistics / Notes</label>
          <textarea value={details.notes || ""} onChange={(e) => setDetails({ ...details, notes: e.target.value })} rows={2} className={inputCls} />
        </div>
      </Section>

      {/* Run of Show */}
      <Section icon={Clock} title="Run of Show" onAdd={() => setRos([...ros, { id: uid(), time: "", item: "", owner: "", location: "", notes: "" }])} onSave={() => save({ run_of_show: ros })} saving={saving}>
        {ros.length === 0 && <p className="text-zinc-600 text-sm">No cues yet. Build the event-day timeline.</p>}
        <div className="space-y-2">
          {ros.map((r) => (
            <div key={r.id} className="flex flex-col md:grid md:grid-cols-12 gap-2 md:items-center" data-testid="ros-row">
              <input value={r.time} onChange={(e) => setRos(ros.map((x) => x.id === r.id ? { ...x, time: e.target.value } : x))} placeholder="Time" className={`${inputCls} col-span-2`} />
              <input value={r.item} onChange={(e) => setRos(ros.map((x) => x.id === r.id ? { ...x, item: e.target.value } : x))} placeholder="Item / cue" className={`${inputCls} col-span-4`} />
              <input value={r.owner} onChange={(e) => setRos(ros.map((x) => x.id === r.id ? { ...x, owner: e.target.value } : x))} placeholder="Owner" className={`${inputCls} col-span-2`} />
              <input value={r.location} onChange={(e) => setRos(ros.map((x) => x.id === r.id ? { ...x, location: e.target.value } : x))} placeholder="Location" className={`${inputCls} col-span-3`} />
              <button onClick={() => setRos(ros.filter((x) => x.id !== r.id))} className="col-span-1 text-zinc-500 hover:text-red-400 flex justify-end"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </Section>

      {/* Vendors — card layout */}
      <Section icon={Truck} title="Vendor Manager" onAdd={addVendor} onSave={() => save({ vendors })} saving={saving}
        extra={isAdmin && (
          <div className="relative">
            <button onClick={() => setShowDir((s) => !s)} className="px-3 py-1.5 bg-white/10 text-white rounded-lg text-xs font-bold hover:bg-white/20 transition flex items-center" data-testid="add-from-directory-btn"><BookMarked size={12} className="mr-1" /> From Directory</button>
            {showDir && (
              <div className="absolute right-0 top-full mt-2 w-72 max-h-72 overflow-y-auto glass-panel bg-[#0a0a0b]/98 border border-white/10 rounded-xl shadow-2xl z-50 p-2 hide-scrollbar" data-testid="vendor-directory-list">
                <div className="flex items-center justify-between px-2 py-1.5">
                  <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider">Vendor Directory</span>
                  <button onClick={() => setShowDir(false)} className="text-zinc-500 hover:text-white"><X size={14} /></button>
                </div>
                {directory.length === 0 && <p className="text-xs text-zinc-500 px-2 py-3">No saved vendors yet. Use the bookmark icon on a vendor to save it here.</p>}
                {directory.map((dv) => (
                  <div key={dv.id} className="flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-white/5">
                    <button onClick={() => addFromDir(dv)} className="flex-1 text-left" data-testid="directory-vendor-add">
                      <div className="text-sm font-bold text-white">{dv.name}</div>
                      <div className="text-[11px] text-zinc-500">{dv.category}{dv.contact ? ` · ${dv.contact}` : ""}</div>
                    </button>
                    <button onClick={() => removeFromDir(dv.id)} className="text-zinc-600 hover:text-red-400"><Trash2 size={13} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      >
        {vendors.length === 0 && <p className="text-zinc-600 text-sm">No vendors yet. Add a vendor card to track their info, contacts and notes.</p>}
        <div className="space-y-3">
          {vendors.map((v) => {
            const open = !!expanded[v.id];
            return (
              <div key={v.id} className="rounded-xl bg-black/30 border border-white/10 overflow-hidden" data-testid="vendor-card">
                <div className="flex flex-col md:grid md:grid-cols-12 gap-2 md:items-center p-3">
                  <button onClick={() => setExpanded((e) => ({ ...e, [v.id]: !open }))} className="col-span-1 text-zinc-400 hover:text-white flex justify-center" data-testid="vendor-card-toggle">{open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</button>
                  <input value={v.name} onChange={(e) => patchVendor(v.id, { name: e.target.value })} placeholder="Vendor name" className={`${inputCls} col-span-4 font-bold`} data-testid="vendor-name" />
                  <input value={v.category || ""} onChange={(e) => patchVendor(v.id, { category: e.target.value })} placeholder="Category" className={`${inputCls} col-span-2`} />
                  <select value={v.status || "pending"} onChange={(e) => patchVendor(v.id, { status: e.target.value })} className={`${inputCls} col-span-2`}>
                    <option value="pending">Pending</option><option value="booked">Booked</option><option value="paid">Paid</option><option value="confirmed">Confirmed</option>
                  </select>
                  <input value={v.cost || ""} onChange={(e) => patchVendor(v.id, { cost: e.target.value })} placeholder="$" className={`${inputCls} col-span-1`} />
                  <div className="col-span-2 flex justify-end gap-1">
                    {isAdmin && <button onClick={() => saveVendorToDir(v)} title="Save to vendor directory" className="text-zinc-500 hover:text-primary-400" data-testid="save-vendor-to-dir"><Bookmark size={15} /></button>}
                    <button onClick={() => setVendors(vendors.filter((x) => x.id !== v.id))} title="Remove vendor" className="text-zinc-500 hover:text-red-400"><Trash2 size={15} /></button>
                  </div>
                </div>
                {open && (
                  <div className="px-3 pb-4 pt-1 border-t border-white/5 space-y-4" data-testid="vendor-card-body">
                    {/* Main info */}
                    <div>
                      <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider">Vendor Info</span>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
                        <input value={v.phone || ""} onChange={(e) => patchVendor(v.id, { phone: e.target.value })} placeholder="Phone" className={inputCls} data-testid="vendor-phone" />
                        <input value={v.email || ""} onChange={(e) => patchVendor(v.id, { email: e.target.value })} placeholder="Email" className={inputCls} data-testid="vendor-email" />
                        <input value={v.address || ""} onChange={(e) => patchVendor(v.id, { address: e.target.value })} placeholder="Address" className={inputCls} data-testid="vendor-address" />
                      </div>
                    </div>
                    {/* Contacts */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider">Contact People</span>
                        <button onClick={() => addContact(v.id)} className="px-2.5 py-1 bg-white/10 text-white rounded-lg text-[11px] font-bold hover:bg-white/20 transition flex items-center" data-testid="add-vendor-contact"><UserPlus size={11} className="mr-1" /> Add Contact</button>
                      </div>
                      {(v.contacts || []).length === 0 && <p className="text-zinc-600 text-xs mb-2">No contacts yet.</p>}
                      <div className="space-y-2">
                        {(v.contacts || []).map((c) => (
                          <div key={c.id} className="flex flex-col md:grid md:grid-cols-12 gap-2 md:items-center" data-testid="vendor-contact-row">
                            <input value={c.name || ""} onChange={(e) => patchContact(v.id, c.id, { name: e.target.value })} placeholder="Name" className={`${inputCls} col-span-3`} />
                            <input value={c.role || ""} onChange={(e) => patchContact(v.id, c.id, { role: e.target.value })} placeholder="Role" className={`${inputCls} col-span-2`} />
                            <input value={c.phone || ""} onChange={(e) => patchContact(v.id, c.id, { phone: e.target.value })} placeholder="Phone" className={`${inputCls} col-span-3`} />
                            <input value={c.email || ""} onChange={(e) => patchContact(v.id, c.id, { email: e.target.value })} placeholder="Email" className={`${inputCls} col-span-3`} />
                            <button onClick={() => removeContact(v.id, c.id)} className="col-span-1 text-zinc-500 hover:text-red-400 flex justify-end"><Trash2 size={13} /></button>
                          </div>
                        ))}
                      </div>
                    </div>
                    {/* Vendor notes — timestamped log */}
                    <div>
                      <label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-2">Notes on this vendor</label>
                      <div className="flex gap-2 mb-2">
                        <input
                          value={vendorNoteDraft[v.id] || ""}
                          onChange={(e) => setVendorNoteDraft((d) => ({ ...d, [v.id]: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addVendorNote(v.id); } }}
                          placeholder="Add a note — it's time-stamped automatically…"
                          className={inputCls}
                          data-testid="vendor-note-input"
                        />
                        <button onClick={() => addVendorNote(v.id)} className="px-3 py-2 bg-primary-600 text-white rounded-lg text-xs font-bold hover:bg-primary-500 transition flex items-center whitespace-nowrap" data-testid="vendor-note-add"><Plus size={12} className="mr-1" /> Add</button>
                      </div>
                      {(v.note_log || []).length === 0 && <p className="text-zinc-600 text-xs">No notes yet.</p>}
                      <div className="space-y-2">
                        {(v.note_log || []).map((n) => (
                          <div key={n.id} className="flex items-start gap-3 p-2.5 rounded-lg bg-black/40 border border-white/5" data-testid="vendor-note-row">
                            <div className="flex-1 min-w-0">
                              <div className="text-sm text-zinc-200 whitespace-pre-wrap break-words">{n.text}</div>
                              <div className="text-[10px] text-zinc-500 mt-1">{fmtTime(n.at)}{n.by ? ` · ${n.by === "admin" ? "Admin" : "Client"}` : ""}</div>
                            </div>
                            <button onClick={() => deleteVendorNote(v.id, n.id)} className="text-zinc-500 hover:text-red-400 flex-shrink-0"><Trash2 size={13} /></button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Section>

      {/* Tasks */}
      <Section icon={ListChecks} title="Task Checklist" onAdd={() => setTasks([...tasks, { id: uid(), title: "", due: "", done: false }])} onSave={() => save({ tasks })} saving={saving}>
        {tasks.length === 0 && <p className="text-zinc-600 text-sm">No tasks yet.</p>}
        <div className="space-y-2">
          {tasks.map((t) => (
            <div key={t.id} className="flex flex-col md:grid md:grid-cols-12 gap-2 md:items-center" data-testid="task-row">
              <input type="checkbox" checked={!!t.done} onChange={(e) => { const nt = tasks.map((x) => x.id === t.id ? { ...x, done: e.target.checked } : x); setTasks(nt); save({ tasks: nt }); }} className="col-span-1 w-4 h-4 accent-primary-500" data-testid="task-checkbox" />
              <input value={t.title} onChange={(e) => setTasks(tasks.map((x) => x.id === t.id ? { ...x, title: e.target.value } : x))} placeholder="Task" className={`${inputCls} col-span-7 ${t.done ? "line-through text-zinc-500" : ""}`} />
              <input type="date" value={t.due || ""} onChange={(e) => setTasks(tasks.map((x) => x.id === t.id ? { ...x, due: e.target.value } : x))} className={`${inputCls} col-span-3`} />
              <button onClick={() => setTasks(tasks.filter((x) => x.id !== t.id))} className="col-span-1 text-zinc-500 hover:text-red-400 flex justify-end"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </Section>

      {/* Budget */}
      <Section icon={DollarSign} title="Budget Tracker" onAdd={() => setBudget([...budget, { id: uid(), description: "", estimated: "", actual: "" }])} onSave={() => save({ budget })} saving={saving}>
        {budget.length === 0 && <p className="text-zinc-600 text-sm">No budget lines yet.</p>}
        <div className="space-y-2">
          {budget.map((b) => (
            <div key={b.id} className="flex flex-col md:grid md:grid-cols-12 gap-2 md:items-center" data-testid="budget-row">
              <input value={b.description} onChange={(e) => setBudget(budget.map((x) => x.id === b.id ? { ...x, description: e.target.value } : x))} placeholder="Line item" className={`${inputCls} col-span-6`} />
              <input type="number" value={b.estimated} onChange={(e) => setBudget(budget.map((x) => x.id === b.id ? { ...x, estimated: e.target.value } : x))} placeholder="Est $" className={`${inputCls} col-span-2`} />
              <input type="number" value={b.actual} onChange={(e) => setBudget(budget.map((x) => x.id === b.id ? { ...x, actual: e.target.value } : x))} placeholder="Actual $" className={`${inputCls} col-span-2`} />
              <div className="col-span-1 text-right text-xs font-bold text-zinc-400">{(Number(b.actual || 0) - Number(b.estimated || 0)) !== 0 ? `${Number(b.actual || 0) - Number(b.estimated || 0) > 0 ? "+" : ""}$${(Number(b.actual || 0) - Number(b.estimated || 0)).toLocaleString()}` : ""}</div>
              <button onClick={() => setBudget(budget.filter((x) => x.id !== b.id))} className="col-span-1 text-zinc-500 hover:text-red-400 flex justify-end"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
        {budget.length > 0 && (
          <div className="flex justify-end gap-6 mt-3 pt-3 border-t border-white/10 text-sm">
            <span className="text-zinc-400">Est: <b className="text-white">${budget.reduce((s, b) => s + Number(b.estimated || 0), 0).toLocaleString()}</b></span>
            <span className="text-zinc-400">Actual: <b className="text-white">${budget.reduce((s, b) => s + Number(b.actual || 0), 0).toLocaleString()}</b></span>
          </div>
        )}
      </Section>

      {/* Receipts & Contracts */}
      <Section icon={ReceiptText} title="Vendor Receipts & Contracts">
        {isAdmin && (
          <div className="mb-4 p-4 rounded-xl bg-black/30 border border-white/10">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
              <div className="md:col-span-3">
                <label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1">Type</label>
                <select value={receiptCat} onChange={(e) => setReceiptCat(e.target.value)} className={inputCls} data-testid="receipt-type-select">
                  <option value="receipt">Receipt</option><option value="contract">Contract</option>
                </select>
              </div>
              <div className="md:col-span-5">
                <label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1">Vendor (optional)</label>
                <input value={receiptVendor} onChange={(e) => setReceiptVendor(e.target.value)} placeholder="e.g. Charter Bus Co." className={inputCls} data-testid="receipt-vendor-input" />
              </div>
              <div className="md:col-span-4">
                <button onClick={() => receiptRef.current?.click()} disabled={uploading} className="w-full px-4 py-2.5 bg-primary-600 text-white rounded-xl text-sm font-bold hover:bg-primary-500 transition flex items-center justify-center disabled:opacity-60" data-testid="receipt-upload-btn">
                  {uploading ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Upload size={15} className="mr-2" />} Upload & Email Client
                </button>
                <input ref={receiptRef} type="file" className="hidden" onChange={(e) => upload(e.target.files?.[0], { category: receiptCat, vendor: receiptVendor, notify: true })} />
              </div>
            </div>
            <p className="text-[11px] text-zinc-500 mt-2 flex items-center gap-1"><Mail size={11} /> The client is automatically emailed with a link when you upload here.</p>
          </div>
        )}
        {receiptFiles.length === 0 && <p className="text-zinc-600 text-sm">No receipts or contracts yet.{isAdmin ? " Upload one above to share it with the client." : ""}</p>}
        <div className="space-y-2">
          {receiptFiles.map((f) => <FileRow key={f.id} f={f} />)}
        </div>
      </Section>

      {/* Documents */}
      <Section icon={FileText} title="Documents & Files">
        <div className="mb-4">
          <button onClick={() => fileRef.current?.click()} disabled={uploading} className="px-4 py-2.5 bg-primary-600 text-white rounded-xl text-sm font-bold hover:bg-primary-500 transition flex items-center disabled:opacity-60" data-testid="ws-upload-btn">
            {uploading ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Upload size={15} className="mr-2" />} Upload File
          </button>
          <input ref={fileRef} type="file" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
        </div>
        {docFiles.length === 0 && <p className="text-zinc-600 text-sm">No files yet. Upload contracts, permits, layouts, etc.</p>}
        <div className="space-y-2">
          {docFiles.map((f) => <FileRow key={f.id} f={f} />)}
        </div>
      </Section>

      {/* Private Notes — admin only, timestamped log, at the very bottom */}
      {isAdmin && (
        <Section icon={Lock} title="Private Notes (internal only)">
          <p className="text-[11px] text-amber-400/80 mb-3 flex items-center gap-1"><Lock size={11} /> Only your team can see this. The client never sees these notes. Each note is time-stamped.</p>
          <div className="flex gap-2 mb-3">
            <textarea
              value={privDraft}
              onChange={(e) => setPrivDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); addPrivNote(); } }}
              disabled={!privLoaded}
              rows={2}
              placeholder="Add an internal note — reminders, margins, vendor issues… (Cmd/Ctrl+Enter to save)"
              className={inputCls}
              data-testid="private-notes-input"
            />
            <button onClick={addPrivNote} disabled={addingPriv || !privDraft.trim()} className="px-4 bg-primary-600 text-white rounded-lg text-xs font-bold hover:bg-primary-500 transition flex items-center whitespace-nowrap disabled:opacity-60" data-testid="private-note-add">
              {addingPriv ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Plus size={13} className="mr-1" />} Add Note
            </button>
          </div>
          {privNotes.length === 0 && <p className="text-zinc-600 text-sm">No private notes yet.</p>}
          <div className="space-y-2">
            {privNotes.map((n) => (
              <div key={n.id} className="flex items-start gap-3 p-3 rounded-xl bg-black/40 border border-amber-500/10" data-testid="private-note-row">
                <Lock size={14} className="text-amber-400/60 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-zinc-200 whitespace-pre-wrap break-words">{n.text}</div>
                  <div className="text-[10px] text-zinc-500 mt-1">{fmtTime(n.at)}</div>
                </div>
                <button onClick={() => deletePrivNote(n.id)} className="text-zinc-500 hover:text-red-400 flex-shrink-0" data-testid="private-note-delete"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}
